<template>
  <div class="staff-request-detail">
    <LoadSpinner v-if="loading" style="padding: 2rem 0" />

    <div v-if="request && employee" class="container">
      <div class="sidebar">
        <EmployeeCard :employee="employee" />
        <br />
        <label>Administrator Notes:</label>
        <md-field class="admin-notes-container">
          <md-textarea
            id="admin-notes-text"
            :disabled="isReadOnlyAdmin()"
            v-model="request.adminNotes"
          ></md-textarea>
        </md-field>
        <div v-if="!isReadOnlyAdmin()" class="admin-notes-container__btn-container">
          <md-button class="md-raised" @click="onSaveAdminNotes" :disabled="updatingNotes">
            Save
          </md-button>
          <p class="text-small text-upper">
            Last updated: {{ request.adminNotesLastUpdated | formatDateShort }}
          </p>
        </div>
        <div v-if="user.isAdmin || user.isStaff" style="padding: 0.9rem 0 0 0">
          <md-datepicker
            :md-clearable="isEditableAdmin"
            v-model="followUpDate"
            md-immediately
            :md-disabled-dates="
              !isEditableAdmin
                ? () => {
                    return true
                  }
                : () => {
                    return false
                  }
            "
          >
            <label>Follow-up date</label>
          </md-datepicker>
        </div>
      </div>

      <div class="request-content">
        <div class="request-content__section">
          <h5 class="text-blue text-upper text-small">Request Detail</h5>
          <div v-if="request.isEditing" class="editing-alert">
            <md-icon class="editing-alert__icon">warning</md-icon>
            <p>User is currently editing request</p>
          </div>
          <div class="title-block">
            <h2 class="title" v-if="request.submittedOn">Request Submitted on {{ request.submittedOn | formatDateShort }}</h2>
            <h2 calss="title" v-else>Request Started on {{ request.datetimeCreated | formatDateShort }}</h2>
            <md-button
              v-if="showReferButton"
              class="md-raised"
              :disabled="request.isReferred || referring"
              @click="onReferRequest"
            >
              <span v-if="!referring">{{ request.isReferred ? 'Referred' : 'Refer' }}</span>
              <LoadSpinner v-else />
            </md-button>
          </div>
          <p>
            <strong>Total Requested: {{ request.amount | currency }}</strong>
          </p>
        </div>

        <!-- Status Section -->
        <div class="request-content__section" v-if="request && request.isClosed">
          <p><strong>Status:</strong> {{ request.status }}</p>
          <p><strong>Payout type:</strong> {{ request.payoutType }}</p>
        </div>

        <div class="request-content__section" v-if="!request.isClosed">
          <div class="request-content__section-top">
            <div v-if="isReadOnlyAdmin()">
              <md-field>
                <label for="status">Status</label>
                <md-input :value="getStatus(editableRequest.status)" :disabled="true"> </md-input>
              </md-field>
            </div>
            <div v-if="!isReadOnlyAdmin()">
              <md-field>
                <label for="payoutType">Payout Type</label>
                <md-select
                  v-model="editableRequest.payoutType"
                  name="payoutType"
                  id="payoutType"
                  :disabled="updating"
                >
                  <md-option
                    v-for="payoutType in Request.options.PAYOUT_TYPE_CHOICES"
                    :value="payoutType.value"
                    :key="payoutType.value"
                    :disabled="updating"
                  >
                    {{ payoutType.label }}
                  </md-option>
                </md-select>
              </md-field>
              <md-field>
                <label for="status">Status</label>
                <md-select
                  v-model="editableRequest.status"
                  name="status"
                  id="status"
                  :disabled="updating"
                >
                  <md-option
                    v-for="statusChoice in availableStatusChoices"
                    :value="statusChoice.value"
                    :key="statusChoice.value"
                    :disabled="updating"
                  >
                    {{ statusChoice.label }}
                  </md-option>
                </md-select>
              </md-field>

              <div
                v-if="
                  editableRequest.status === Request.options.PARTIALLY_APPROVED ||
                    editableRequest.status === Request.options.PARTIALLY_APPROVED_PENDING
                "
              >
                <md-field>
                  <div class="RequestDetail__USD">
                    <span class="RequestDetail__USD__text">US$</span>
                  </div>
                  <label>Amount to Disburse</label>
                  <md-input
                    type="number"
                    v-model="editableRequest.amount"
                    :disabled="updating"
                  ></md-input>
                </md-field>
              </div>

              <div v-if="isTcrShefRequest">
                <md-checkbox
                  v-model="editableRequest.requireAdditionalDocumentation"
                  :disabled="updating"
                  class="require-additional-documentation"
                >Flag require receipt?</md-checkbox>
              </div>
            </div>

            <div v-if="!isReadOnlyAdmin()">
              <md-field>
                <label for="assigned">Assigned Staff</label>
                <md-select
                  name="assigned"
                  id="assigned"
                  v-model="request.assignedTo"
                  @md-selected="onUpdateAssigned"
                >
                  <md-option v-for="staff in staff.list" :value="staff.id" :key="staff.id">
                    {{ staff.fullName }}
                  </md-option>
                </md-select>
                <div @click="onResetAssigned" class="icon-container" v-if="request.assignedTo">
                  <md-icon style="color: gray">close</md-icon>
                </div>
              </md-field>
            </div>
          </div>

          <div v-if="editableRequest.status === Request.options.DECLINED || editableRequest.status === Request.options.DECLINED_NOTIFY">
            <md-field>
              <label for="declined-reason">Declined Reason</label>
              <md-select
                v-model="editableRequest.declinedReason"
                name="declined-reason"
                id="declined-reason"
                :disabled="updating"
              >
                <md-option
                  v-for="reasonDeclined in Request.options.REASON_DECLINED_CHOICES"
                  :value="reasonDeclined.value"
                  :key="reasonDeclined.value"
                >
                  {{ reasonDeclined.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div v-if="editableRequest.hasEdit">
            <md-button
              class="md-raised md-primary"
              :disabled="updating || isReadOnlyAdmin()"
              @click="updateStatus"
              >Decision</md-button
            >
          </div>
        </div>
        <!-- End Status Section -->

        <!-- Request view modes -->
        <div class="request-content__section request-content__section--bordered">
          <div style="display: flex">
            <md-button
              @click="() => (mode = EVENT_DETAIL_MODE)"
              :class="{ 'md-primary': mode === EVENT_DETAIL_MODE }"
              style="margin-left: 0"
            >
              <i
                class="text-bold text-upper text-small"
                :class="{ 'text-blue': mode !== EVENT_DETAIL_MODE }"
              >
                Request Details
              </i>
            </md-button>
            <md-button
              v-if="showAdditionalDocumentation"
              @click="() => (mode = ADDITIONAL_DOCUMENTATION_MODE)"
              :class="{ 'md-primary': mode === ADDITIONAL_DOCUMENTATION_MODE }"
              style="margin-left: 0"
            >
              <i
                class="text-bold text-upper text-small"
                :class="{ 'text-blue': mode !== ADDITIONAL_DOCUMENTATION_MODE }"
              >
                Additional Documentation
              </i>
            </md-button>
            <md-button
              v-if="showMessages"
              @click="() => (mode = MESSAGE_MODE)"
              :class="{ 'md-primary': mode === MESSAGE_MODE }"
            >
              <i
                class="text-bold text-upper text-small"
                :class="{ 'text-blue': mode !== MESSAGE_MODE }"
              >
                {{ communicationName }}
              </i>
            </md-button>
            <md-button
              v-if="showEmails"
              @click="() => (mode = EMAIL_MODE)"
              :class="{ 'md-primary': mode === EMAIL_MODE }"
            >
              <i
                class="text-bold text-upper text-small"
                :class="{ 'text-blue': mode !== EMAIL_MODE }"
              >
                Email
              </i>
            </md-button>
            <md-button
              v-if="showFollowUp"
              @click="() => (mode = FOLLOW_UP_MODE)"
              :class="{ 'md-primary': mode === FOLLOW_UP_MODE }"
            >
              <i
                class="text-bold text-upper text-small"
                :class="{ 'text-blue': mode !== FOLLOW_UP_MODE }"
              >
                Follow-Up Notes
              </i>
            </md-button>
          </div>
        </div>

        <!-- Emails -->
        <div class="request-content__section" v-if="mode === EMAIL_MODE">
          <div>
            <md-field>
              <label for="email-list">Emails</label>
              <md-select
                v-model="emailType"
                name="email-choice"
                id="email-choice"
                :disabled="updating"
                @md-selected="emailTypeSelected"
              >
                <md-option
                  v-for='emailSelectType in emailTypes'
                  :value="emailSelectType"
                  :key="emailSelectType"
                >
                  {{ emailSelectType }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="request-content__section" v-if="mode === EMAIL_MODE">
            <div v-html="selectedEmailContent">
            </div>
          </div>


        </div>

        <!-- Messages -->
        <div class="request-content__section" v-if="mode === MESSAGE_MODE && !isReadOnlyAdmin()">
          <MessageForm :request="request" :msgUser="request.employeeRef" type="request" @created="onMessageCreated" :staff-view="true" />
        </div>
        <div class="request-content__section" v-if="mode === MESSAGE_MODE">
          <MessagesList
            :messages="messages"
            @next-page-loaded="markAllMessagesRead"
            @message-deleted="refreshMessages"
            :staff-view="true"
          />
        </div>

        <!-- Event categories, open-ended question & event questions -->
        <div class="request-content__section" v-if="mode === EVENT_DETAIL_MODE">
          <h4 class="text-normal reportable-categories">Events</h4>

          <!-- If request is closed, display list of reportable event category names -->
          <template v-if="request.isClosed || isReadOnlyAdmin()">
            <md-field style="flex-direction: column;">
              <p v-for="eventCategory in eventCatNames" :key="eventCategory" style="margin-bottom: 0;">
                {{ eventCategory }}
              </p>
            </md-field>
          </template>

          <!-- If request is open, display selectable list of reportable event category names -->
          <md-field v-else>
            <md-select
              v-model="reportableEventNames"
              md-dense
              multiple
              @md-selected="onSelectReportableEventCategory"
              :disabled="updating"
            >
              <md-option
                v-for="eventCategory in eventCategories"
                :key="eventCategory.id"
                :value="eventCategory.name"
              >
                {{ eventCategory.name }}
              </md-option>
            </md-select>
          </md-field>

          <div class="text-margin-vertical">
            <h4 class="text-normal reportable-categories" style="margin-bottom: 1rem">
              Open-ended Question
            </h4>
            <p class="text-margin-vertical">
              <em>Tell us about your emergency. Please write a few sentences to explain what happened, why it's an emergency, and how emergency cash can help.</em><br />
              {{ request.emergencyDescription || 'No Response' }}
            </p>
          </div>

          <div class="text-margin-vertical" v-for="event in eventsRef" :key="event.id">
            <div>
              <h4 class="text-normal reportable-categories" style="margin-bottom: 1rem">{{ event.categoryRef.name }}</h4>
              <p class="text-margin-vertical">
                <em>When it happened:</em> <br />
                {{ event.dateOccurred || 'No Response' }}
              </p>
            </div>

            <p v-for="question in event.questions" :key="question.id" class="text-margin-vertical">
              <em>{{ question.text }}</em> <br />{{
                question.answer.text || question.answer.text || 'No Response'
              }}
            </p>

            <div class="file-picker">
              <FilePicker
                ref="chooser"
                :readAsString="false"
                @loaded="onEventDocumentUpload(event, $event)"
                :styles="'secondary'"
                v-if="showDocumentUpload"
              />
            </div>

            <document-download
              v-for="document in event.documentsRef"
              :document="document"
              :key="document.id"
            />

            <p class="text-margin-vertical">
              <em>
                Please add any additional information that will help us better understand your
                document(s)
              </em>
              <br />
              {{ request.eventDocumentationMisc || 'No Response' }}
            </p>
          </div>
        </div>

        <!-- Request questions -->
        <div class="request-content__section" v-if="mode === EVENT_DETAIL_MODE">
          <h4 class="text-normal reportable-categories" style="margin-bottom: 1rem">
            General Questions
          </h4>
          <template v-if="request.requestQuestions.length">
            <template v-for="question in request.requestQuestions">
              <p class="text-margin-vertical" :key="question.id">
                <em>
                  {{ question.text }}
                </em>
                <br />
                {{ question.answer.text || question.answer.text || 'No Response' }}
              </p>
            </template>
          </template>
          <template v-if="!request.requestQuestions.length">
            <p class="text-margin-vertical">
              <em>
                Please explain how the emergency (or emergencies) you selected created a financial
                hardship for you and your household? That is, how did they make it hard for you to
                afford basic living expenses?
              </em>
              <br />
              {{ request.eventsFinancialHardshipAnswer || 'No Response' }}
            </p>

            <p class="text-margin-vertical">
              <em>Is there anything else we need to know about your emergency or emergencies?</em>
              <br />
              {{ request.eventsMisc || 'No Response' }}
            </p>

            <p class="text-margin-vertical">
              <em
                >Was at least one of the emergencies described above unexpected or outside of your
                control?</em
              >
              <br />
              {{ request.eventsUnexpectedOrUncontrollableMisc || 'No Response' }}
            </p>

            <p class="text-margin-vertical">
              <em
                >Has the emergency (or emergencies) made it difficult for you to afford basic living
                expenses?</em
              >
              <br />
              {{ request.eventsBasicLivingDifficultiesMisc || 'No Response' }}
            </p>
          </template>
        </div>

        <!-- Event details -->
        <div
          class="request-content__section request-content__section--bordered"
          v-if="mode === EVENT_DETAIL_MODE"
        >
          <h5 class="text-blue text-upper text-small">Expense Amount and Documentation</h5>

          <div>
            <p class="text-margin-vertical">
              <em>
                Please add any additional information that will help us better understand your
                document(s):
              </em>
              <br />
              {{ request.expenseDocumentationMisc || 'No Response' }}
            </p>
          </div>

          <div v-for="expense in expensesRef" :key="expense.id" style="margin-bottom: 1rem">
            <h4 class="text-normal reportable-categories" style="margin-bottom: 1rem;">{{ expense.categoryRef.name }}</h4>
            <p class="text-margin-vertical">Amount Requested: {{ expense.amount | currency }}</p>

            <div class="file-picker">
              <FilePicker
                ref="chooser"
                :readAsString="false"
                @loaded="onExpenseDocumentUpload(expense, $event)"
                :styles="'secondary'"
                v-if="showDocumentUpload"
              />
            </div>

            <document-download
              v-for="document in expense.documentsRef"
              :document="document"
              :key="document.id"
            />
          </div>
        </div>

        <!-- Additional documentation -->
        <div class="request-content__setion" v-if="mode === ADDITIONAL_DOCUMENTATION_MODE">
          <div>
            <h4 class="normal-text">Additional Documentation</h4>
          </div>

          <document-download
            v-for="document in request.documentsRef"
            :document="document"
            :key="document.id"
          />

          <md-button v-if="request.requireAdditionalDocumentation" @click="handleAdditionalDocumentationCompleted" class="md-primary">Documentation Is Sufficient</md-button>
        </div>

        <!-- Follow-Up Notes -->
        <div class="request-content__setion" v-if="mode === FOLLOW_UP_MODE">
          <div class="follow-up-note-form-container">
            <div class="follow-up-note-form-toggle" @click="toggleShowFollowUpNoteForm">
              <div>
                <span>Add a Follow-Up Note</span>
              </div>
              <div>
                <md-icon v-if="!showFollowUpNoteForm">expand_more</md-icon>
                <md-icon v-else>expand_less</md-icon>
              </div>
            </div>
            <div class="follow-up-note-form" v-if="showFollowUpNoteForm">
              <label for="follow-up-note-text">Note:</label>    
              <md-field>

                <md-textarea
                  id="follow-up-note-text"
                  v-model="followUpNoteText"
                ></md-textarea>
              </md-field>
              <md-button @click="addFollowUpNote" class="md-primary add-follow-up-note-button">Add</md-button>
            </div>
          </div>

          <h4 class="normal-text">Follow-Up Notes</h4>
          <template v-if="!this.followUpNotes.list.length">
            There are no follow-up notes yet.
          </template>
          <template v-else>
            <md-table>
              <md-table-row class="header-row">
                <md-table-head>
                  Date
                </md-table-head>
                <md-table-head>
                  Added By
                </md-table-head>
                <md-table-head>
                  Note
                </md-table-head>
              </md-table-row>
              <md-table-row class="body-row" v-for="followUpNote in followUpNotes.list" :key="followUpNote.id">
                <md-table-cell class="follow-up-note-date">{{ followUpNote.datetimeCreated | formatDateShort }}</md-table-cell>
                <md-table-cell class="follow-up-note-user">{{ followUpNote.userFullName }}</md-table-cell>
                <md-table-cell>{{ followUpNote.note }}</md-table-cell>
              </md-table-row>
            </md-table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { DocumentDownload, LoadSpinner } from '@/components'
import FilePicker from '@/components/FilePicker'
import { formatNumberAsUSD } from '@/services/utils'

import CollectionManager from '@/services/collectionManager'

import Employee from '@/services/employees'
import EventCategory from '@/services/eventCategories'
import Request from '@/services/requests'
import Message from '@/services/messages'
import FollowUpNote from '@/services/followUpNotes'
import Staff from '@/services/staff'
import Document from '@/services/documents'
import EmailAPI from '@/services/emails/api'

import EmployeeCard from '@/views/common/EmployeeCard'
import MessageForm from '@/views/common/MessageForm'
import MessagesList from '@/views/common/MessagesList'
import AdminPermissions from '@/services/adminPermissions'

import { translate } from '@/services/translations'
const $gettext = translate.gettext.bind(translate)

const EVENT_DETAIL_MODE = 'EVENT_DETAIL'
const MESSAGE_MODE = 'MESSAGE'
const EMAIL_MODE = 'EMAIL'
const ADDITIONAL_DOCUMENTATION_MODE = 'ADDITIONAL_DOCUMENTATION_MODE'
const FOLLOW_UP_MODE = 'FOLLOW_UP'

export default {
  name: 'RequestDetail',
  components: {
    DocumentDownload,
    LoadSpinner,
    EmployeeCard,
    MessageForm,
    MessagesList,
    FilePicker,
  },
  data() {
    return {
      Request,

      EVENT_DETAIL_MODE,
      MESSAGE_MODE,
      EMAIL_MODE,
      ADDITIONAL_DOCUMENTATION_MODE,
      FOLLOW_UP_MODE,
      mode: EVENT_DETAIL_MODE,

      emailType: null,
      emailTypes: [
        'WELCOME',
        'EMPLOYEE_ACCOUNT_CREATED',
        'EMPLOYEE_NEW_DOCUMENTATION_MESSAGE',
        'EMPLOYEE_NEW_GRANT_DECISION_MESSAGE',
        'EMPLOYEE_NEW_REFERRAL_MESSAGE',
        'EMPLOYEE_NEW_OTHER_MESSAGE',
        'EMPLOYEE_WELCOME_SMS_MESSAGE',
        'REQUEST_SUBMITTED_EMPLOYEE',
        'REQUEST_SUBMIT_REMINDER',
        'REQUEST_INCOMPLETE_SURVEY',
        'NO_DOCUMENTATION_REQUEST_SUBMITTED_EMPLOYEE',
        'SURVEY_REMINDER',
        'POST_CONTRIBUTION_RECURRING_ABSOLUTE_SURVEY',
        'POST_CONTRIBUTION_ONE_TIME_ABSOLUTE_SURVEY',
        'POST_REQUEST_APPROVED_SURVEY',
        'WDP_POST_REQUEST_APPROVED_SURVEY',
        'POST_REQUEST_DECLINED_SURVEY',
        'POST_REQUEST_FOUR_MONTH_SURVEY'
      ],

      selectedEmailContent: '',

      request: null,
      editableRequest: null,
      employee: null,
      eventCategories: null,

      messages: CollectionManager.create({
        ModelClass: Message,
        filters: { ordering: '-datetime_created' },
      }),

      staff: CollectionManager.create({
        ModelClass: Staff,
      }),

      followUpDate: null,

      loading: false, // initial page load
      refreshing: false, // refreshing request data
      updating: false, // updating request status
      referring: false, // referring request
      updatingNotes: false,
      reportableEventNames: [],
      eventsRef: null,
      expensesRef: null,
      reportableEventCategoriesRef: null,

      /* Follow-Up Notes */
      followUpNotes: CollectionManager.create({
        ModelClass: FollowUpNote,
        filters: { ordering: '-datetime_created' },
      }),
      showFollowUpNoteForm: false,
      followUpNoteText: '',
    }
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    availableStatusChoices() {
      // Remove certain statuses from the dropdown menu
      var statuses = Request.options.REQUEST_STATUS_CHOICES.filter(
        c => ![Request.options.STARTED].includes(c.value),
      )

      if (this.request.organizationRef.isHeartstringsOrganization) {
        statuses = statuses.concat({ value: Request.options.DECLINED_NOTIFY, label: $gettext('Declined - Notify') })
      }

      return statuses
    },
    showReferButton() {
      // For staff, only show button if request can be or has been referred.
      if (this.user.isStaff) {
        return this.request.canBeReferred

        // For admins, only show button if request has been referred.
      } else if (this.user.isAdmin) {
        return this.request.isReferred
      }
      return false
    },
    communicationName() {
      if (this.user.isStaff) {
        return 'Communication'
      }

      return 'Messages'
    },
    showEmails() {
      if (this.user.isStaff) {
        return true
      }

      return false
    },
    showMessages() {
      if (this.user.isStaff) {
        return true
      } else if (this.user.isAdmin) {
        return this.user.adminPermissions.canViewMessages
      }
      return false
    },
    showDocumentUpload() {
      if (this.user.isStaff) {
        return true
      } else if (this.user.isAdmin) {
        return this.user.adminPermissions.name === 'MASTER'
      }
      return false
    },
    legacyRequestQuestionsCheck() {
      return (
        (this.request &&
          this.request.eventsFinancialHardshipAnswer &&
          this.request.eventsFinancialHardshipAnswer != '') ||
        (this.request.eventsMisc && this.request.eventsMisc != '') ||
        (this.request.eventsUnexpectedOrUncontrollableMisc &&
          this.request.eventsUnexpectedOrUncontrollableMisc != '') ||
        (this.request.eventsBasicLivingDifficultiesMisc &&
          this.request.eventsBasicLivingDifficultiesMisc != '')
      )
    },
    isEditableAdmin() {
      if (
        this.user.isStaff ||
        (this.user.isAdmin &&
          this.user.adminPermissions.name == AdminPermissions.permissionNames.MASTER)
      ) {
        return true
      }
      return false
    },
    eventCatNames() {
      if (!this.reportableEventCategoriesRef)
        return []
      
      return this.reportableEventCategoriesRef.map(match => {
        return match.name
      })
    },
    showAdditionalDocumentation() {
      return this.request.organizationRef.isTcrShef && this.request.documentsRef.length
    },
    isTcrShefRequest() {
      if (!this.request)
        return false

      return this.request.organizationRef.isTcrShef
    },

    /* Follow-Up Notes */
    showFollowUp() {
      if (this.user.isStaff) {
        return true
      }

      return false
    },
  },
  created() {
    this.loading = true
    this.refresh().finally(() => {
      this.loading = false
      this.logAdminView()
      this.setDefaultView()
    })
    const refreshEditing = this.refreshEditing
    this.refreshInterval = setInterval(function() {
      refreshEditing()
    }, 15000)
    this.staff.refresh()
  },
  destroyed() {
    clearInterval(this.refreshInterval)
  },
  methods: {
    async emailTypeSelected() {
      const emailApi = new EmailAPI
      const emailContent = await emailApi.getEmail(
          this.request.id,
          this.emailType,
          this.request.employeeRef.id,
          this.request.organizationRef.id,
        )

      this.selectedEmailContent = emailContent.email_html
    },
    setDefaultView() {
      if(this.showAdditionalDocumentation)
        this.mode = ADDITIONAL_DOCUMENTATION_MODE
    },
    convertCatNameToId() {
      let eventCatIds = []
      if (this.eventCategories) {
        this.reportableEventNames.forEach(name => {
          const cat = this.eventCategories.find(cat => {
            return cat.name == name
          })
          if (cat) {
            eventCatIds.push(cat.id)
          }
        })
        if (eventCatIds.length == this.reportableEventNames.length) {
          this.editableRequest.reportableEventCategories = eventCatIds
        }
      }
    },
    orderRequestQuestions() {
      var last_item = this.request.requestQuestions.splice(
        this.request.requestQuestions.findIndex(question => question.order == -1),
        1,
      )
      this.request.requestQuestions.sort((first, second) => first.order - second.order)
      this.request.requestQuestions = this.request.requestQuestions.concat(last_item)
    },
    getStatus(requestStatus) {
      const foundStatus = Request.options.REQUEST_STATUS_CHOICES.find(
        status => status.value === requestStatus,
      )

      return foundStatus && foundStatus.label
    },
    isReadOnlyAdmin() {
      if (
        this.user.isAdmin &&
        (
          this.user.adminPermissions.name === AdminPermissions.permissionNames.READ_ONLY
          || this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN
        )
      ) {
        return true
      }

      return false
    },
    refresh(showRefreshing=true) {
      if(!showRefreshing)
        this.refreshing = true
      const requestPromise = Request.api
        .retrieve(this.$route.params.id)
        .then(request => {
          this.request = request
          this.orderRequestQuestions()
          this.editableRequest = !this.request.isClosed
            ? Request.create({
                ...request,
                ...(request.declinedReason
                  ? {}
                  : { declinedReason: Request.options.DOCUMENTATION_INSUFFICIENT_OR_UNCLEAR }),
              })
            : null
          return request
        })
        .then(request => {
          return Employee.api.retrieve(request.employeeRef.id)
        })
        .then(employee => {
          this.employee = employee
        })

      // Load event references
      Request.api.retrieveEvents(this.$route.params.id).then(eventsRef => {
        this.eventsRef = eventsRef
      })

      // Load expense references
      Request.api.retrieveExpenses(this.$route.params.id).then(expensesRef => {
        this.expensesRef = expensesRef
      })

      // Load the reportable event categories references
      Request.api.retrieveReportableEventCategories(this.$route.params.id).then(reportableEventCategoriesRef => {
        this.reportableEventCategoriesRef = reportableEventCategoriesRef
      })

      // Load messages
      this.refreshMessages()

      // Load follow-up notes
      this.refreshFollowUpNotes()

      // Refreshing is done once all of the above promises are done.
      return Promise.all([requestPromise]).finally(() => {
        this.refreshing = false
      })
    },
    refreshMessages() {
      this.messages.filters.request = this.$route.params.id
      return this.messages.refresh()
    },
    refreshEditing() {
      if (this.request) {
        Request.api.retrieve(this.$route.params.id).then(request => {
          this.request.isEditing = request.isEditing
        })
      }
    },
    onMessageCreated(message) {
      this.refreshMessages()
    },
    onSelectReportableEventCategory() {
      this.updating = true
      this.convertCatNameToId()
      Request.api
        .update(this.editableRequest, ['reportableEventCategories'])
        .then(() => {
          this.request.reportableEventCategories = this.editableRequest.reportableEventCategories
        })
        .finally(() => {
          this.updating = false
        })
    },
    onSaveAdminNotes() {
      this.updatingNotes = true
      Request.api.update(this.request, ['adminNotes']).then(() => {
        this.refresh(false)
      })
      this.updatingNotes = false
    },
    updateStatus() {
      // Minimal validation

      if (
        (this.editableRequest.status === Request.options.DECLINED ||
        this.editableRequest.status === Request.options.DECLINED_NOTIFY) &&
        !this.editableRequest.declinedReason
      ) {
        this.$Alert.alert({
          type: 'warning',
          message: `
            <h4>Error</h4>
            <p>Please select the reason why you are declining this request.</p>
          `,
          timeout: 5000,
        })
      }

      if (this.editableRequest.amount < 0) {
        this.$Alert.alert({
          type: 'warning',
          message: `
            <h4>Error</h4>
            <p>You may not reimburse a negative amount. Please enter a
               positive number and try again.</p>
          `,
          timeout: 5000,
        })
        return
      }

      if (this.editableRequest.amount > this.request.amount) {
        this.$Alert.alert({
          type: 'warning',
          message: `
            <h4>Error</h4>
            <p>You may not reimburse an amount greater than the requested amount.
               Please update it and try again.</p>
          `,
          timeout: 5000,
        })
        return
      }

      if (this.organizationDetails.grantLimit) {
        if (
          this.editableRequest.amount + this.user.totalPayoutsSinceLastGrantLimitReset >
          this.organizationDetails.grantLimit
        ) {
          let max_amount =
            this.organizationDetails.grantLimit - this.user.totalPayoutsSinceLastGrantLimitReset
          let max_amount_formatted = formatNumberAsUSD(max_amount)
          this.$Alert.alert({
            type: 'warning',
            message: `
              <h4>Error</h4>
              <p>You may not reimburse an amount greater than the grant limit.
                Please update it and try again. (Maximum: ${max_amount_formatted})</p>
            `,
            timeout: 5000,
          })
          this.editableRequest.amount = max_amount
          return
        }
      }

      if (this.organizationDetails.grantLimitLifetime) {
        if (
          this.editableRequest.amount + this.user.totalPayouts >
          this.organizationDetails.grantLimitLifetime
        ) {
          let max_amount =
            this.organizationDetails.grantLimitLifetime - this.user.totalPayouts
          let max_amount_formatted = formatNumberAsUSD(max_amount)
          this.$Alert.alert({
            type: 'warning',
            message: `
              <h4>Error</h4>
              <p>You may not reimburse an amount greater than the grant limit.
                Please update it and try again. (Maximum: ${max_amount_formatted})</p>
            `,
            timeout: 5000,
          })
          this.editableRequest.amount = max_amount
          return
        }
      }

      this.updating = true
      this.$store.state.invalidateAxiosApi = true
      
      Request.api
        .updateStatus(this.editableRequest)
        .then(() => {
          this.refresh()

          // If user can send messages, direct them to send one after updating status
          if (this.showMessages) {
            this.mode = MESSAGE_MODE
            this.$Alert.alert({
              type: 'success',
              message: `
              <h4>Status updated!</h4>
              <p>Employees are <em>not</em> automatically notified, so you may want
                 to write them a message.</p>
            `,
              timeout: 6000,
            })
          } else {
            this.$Alert.alert({
              type: 'success',
              message: `
              <h4>Status updated!</h4>
              <p>Note: Employees are not automatically notified.</p>
            `,
              timeout: 6000,
            })
          }
        })
        .finally(() => {
          this.updating = false
        })
    },
    markAllMessagesRead() {
      // Mark all unread messages as "read" when the user
      // switches to the 'Messages' view.
      this.messages.list
        .filter(m => m.isUnread)
        .forEach(m => {
          m.isUnread = false
          Message.api.markRead(m).catch(() => {
            m.isUnread = true
          })
        })
    },
    onReferRequest() {
      this.referring = true
      Request.api
        .refer(this.editableRequest)
        .then(() => {
          this.refresh()
          this.$Alert.alert({
            type: 'success',
            message: `<h4>Request referred!</h4>`,
            timeout: 5000,
          })
        })
        .finally(() => {
          this.referring = false
        })
    },
    async logAdminView() {
      await Request.api.logAdminView(this.request)
    },
    onUpdateAssigned() {
      Request.api.update(this.request, ['assignedTo']).then(() => {
        this.refresh()
      })
    },
    onResetAssigned() {
      this.request.assignedTo = ''
      this.onUpdateAssigned()
    },
    onEventDocumentUpload(event, file) {
      const doc = Document.create({
        file: file,
        filename: file.name,
      })
      doc.event = event.id
      Document.api.create(doc).then(document => event.documentsRef.push(document))
    },
    onExpenseDocumentUpload(expense, file) {
      const doc = Document.create({
        file: file,
        filename: file.name,
      })
      doc.expense = expense.id
      Document.api.create(doc).then(document => expense.documentsRef.push(document))
    },
    formatDateForPicker(date) {
      var monthString = `${this.request.followUpDate.getUTCMonth() + 1}`
      if (monthString.length == 1) {
          monthString = "0" + monthString
      }
      return `${this.request.followUpDate.getUTCFullYear()}-${monthString}-${this.request.followUpDate.getUTCDate()}`
    },
    async handleAdditionalDocumentationCompleted() {
      await Request.api.setAdditionalDocumentationSufficient(this.request)
      this.refresh()
    },

    /* Follow-Up Notes */
    refreshFollowUpNotes() {
      this.followUpNotes.filters.request = this.$route.params.id
      return this.followUpNotes.refresh()
    },
    toggleShowFollowUpNoteForm() {
      this.showFollowUpNoteForm = !this.showFollowUpNoteForm
    },
    addFollowUpNote() {
      let note = this.followUpNoteText
      this.followUpNoteText = ''
      FollowUpNote.api.create(
        FollowUpNote.create({
          note: note,
          request: this.$route.params.id,
        })
      ).then(followUpNote => {
        this.followUpNotes.refresh()
      })
    }
  },
  watch: {
    mode(newVal) {
      if (newVal === MESSAGE_MODE) {
        this.markAllMessagesRead()
      }
    },
    request() {
      EventCategory.api.list({
        filters: { event_category_organization: this.request.organization },
        }).then(eventCategories => {
          this.eventCategories = eventCategories.results
        }).then(() => {
          this.reportableEventNames = this.eventCatNames
        })

        if (this.request.followUpDate) {
          this.followUpDate = this.formatDateForPicker(this.request.followUpDate)
        }
    },
    followUpDate: {
      async handler(newFollowUpDate, oldFollowUp) {
        if (!oldFollowUp) { return }

        oldFollowUp = new Date(oldFollowUp)
        const newFollowUp = new Date(newFollowUpDate)

        const now = new Date()

        if (!this.isEditableAdmin) return
        if (this.request == null) return
        if (!newFollowUp || !oldFollowUp) return


        oldFollowUp.setUTCHours(0,0,0,0)

        if (new Date(newFollowUp) == new Date(oldFollowUp)) return
        if (this.request.followUpDate == 'undefined') return

        // Update the follow up date
        await Request.api
          .adminPartialUpdate({ followUpDate: newFollowUp.toISOString(), id: this.request.id }, [
            'followUpDate',
          ])
          .catch(error => {
            this.request.followUpDate = oldFollowUp
            throw error
          })
      },
    },
    editableRequest: {
      handler(newVal, oldVal) {
        if (oldVal === null || newVal === null) return
        this.editableRequest.hasEdit = true

        // Reset the amount on the editable request if the
        // user changes the status away from 'Partially Approved'
        // or 'Partially Approved Pending'.
        if (
          (oldVal.status === Request.options.PARTIALLY_APPROVED ||
            oldVal.status === Request.options.PARTIALLY_APPROVED_PENDING) &&
          newVal.status !== Request.options.PARTIALLY_APPROVED &&
          newVal.status !== Request.options.PARTIALLY_APPROVED_PENDING
        ) {
          this.editableRequest.amount = this.request.amount
        }

        // Reset the declined reason on the editable request if the
        // user changes the status away from 'Declined'.
        if (
          oldVal.status === Request.options.DECLINED &&
          newVal.status !== Request.options.DECLINED
        ) {
          this.editableRequest.declinedReason = this.request.declinedReason
        }
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/mixins/containers';
@import '@/styles/variables';

.title-block {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 2.5rem;
}
.title {
  padding-right: 1rem;
  margin-bottom: 0rem;
  // ^For some reason this h2 tag has a margin-bottom unless I explicitly remove it
}
.staff-request-detail {
  @include screen-container;
}

.container {
  @include flex-container;
}

.request-content {
  flex: 12;

  &__section {
    padding: 1rem;

    &--bordered {
      padding-top: 2rem;
      border-top: 1px solid $soft-gray;
    }

    &-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}

.reportable-categories {
  margin-bottom: -1.5rem;
  font-weight: bold;
}

// HACK: The material design package or theme does not
//       have working styles by default. For instance,
//       the pop-up menu background is transparent!
// ^ Dumb, didn't understand the docs...
.md-field > label {
  font-weight: bold;
  color: $base-gray;
}

.md-field {
  max-width: 26rem;
}
.md-field .md-input {
  margin-right: 1rem;
}

.md-field .md-textarea {
  max-height: 35rem;
  font-size: 14px;
  line-height: 1rem;
}

.md-menu.md-select {
  padding: 0.25rem 0.5rem;
  margin-right: 1rem;
}

.admin-notes-container {
  max-width: 24rem;
  margin-bottom: 0;

  &__btn-container {
    display: flex;
    justify-content: space-between;
  }
}
#admin-notes-text {
  min-height: 12rem;
}

// END HACK

.sidebar {
  padding: 0 1rem 0 0;
}

.editing-alert {
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fff3cd;
  justify-content: flex-start;
  width: 100%;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 8px;

  &__icon {
    margin: 4px;
  }
}

.file-picker {
  width: 10rem;
}

.require-additional-documentation {
  color: red;
}

.RequestDetail__USD {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin-right: 8px;
}

.RequestDetail__USD__text {
  font-style: italic;
}

.follow-up-note-form-container {
  background-color: $soft-gray;
  border-radius: 1rem;
  color: white;
  margin-bottom: 1rem;
  overflow: hidden;
  width: 100%;

  .follow-up-note-form-toggle {
    display: flex;
    justify-content: space-between;
    background-color: #3775dd;
    padding: 0.5rem 1rem;

    span, i {
      color: white;
      font-weight: bold;
    }
  }

  .follow-up-note-form {
    padding: 1rem;

    label {
      color: black;
      font-weight: bold;
    }

    .add-follow-up-note-button {
      align-self: right;
    }
  }

  .md-field {
    max-width: unset;
  }
}

// Table Styles
.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 0.9rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem;
  }

  &:hover {
    background-color: $off-white;
  }
}

.follow-up-note-date {
  width: 150px;
}

.follow-up-note-user {
  width: 200px;
}
</style>
